import React from 'react';
import { StyledLink } from './styles';
import { IconContainer } from './containers';
import Icon from '../../Icon';
import Typo from '../../Typo';
import { Tooltip } from '@mui/material';

// Shared utility function for icon size
const getIconSize = (size) => {
  switch(size) {
    case 'x-small': return 'x-small';
    case 'small': return 'x-small';
    case 'large': return 'small';
    default: return 'small';
  }
};

/**
 * ButtonLink - Link styled as a button
 * 
 * @param {Object} props
 * @param {string} props.variant - Button style: 'primary', 'secondary', 'action', 'outline', 'themed', 'asText'
 * @param {string} props.size - Button size: 'small', 'medium', 'large'
 * @param {string} props.startIcon - Name of icon to display before text (e.g. 'Search', 'Download')
 * @param {string} props.endIcon - Name of icon to display after text (e.g. 'ArrowRight', 'Check')
 * @param {string} props.icon - Name of icon for icon-only button (e.g. 'Search', 'Add')
 * @param {string} props.themeColor - Custom theme color (for themed variant)
 * @param {string} props.bgColor - Background color for certain variants
 * @param {boolean} props.isLoading - Whether button is in loading state
 * @param {boolean} props.fullWidth - Whether button takes full width
 * @param {string} props.href - URL for the link (required)
 * @param {boolean} props.newTab - Whether link opens in new tab
 * @param {string} props.aria-label - Required for icon-only buttons for accessibility
 * @param {string} props.tooltipPlacement - Position of tooltip: 'top', 'bottom', 'left', 'right'
 */
const ButtonLink = ({
  children,
  variant = 'primary',
  size = 'medium',
  startIcon,
  endIcon,
  icon, // For icon-only buttons
  themeColor,
  bgColor,
  isLoading = false,
  fullWidth = false,
  square = false,
  href, // Required for links
  newTab,
  onClick,
  tooltipPlacement = 'bottom',
  ...rest
}) => {
  // Check if this is an icon-only button
  const isIconOnly = !children && (icon || startIcon);
  const iconToUse = icon || startIcon;
  
  // Get the icon components if icon names are provided
  const IconComponent = isIconOnly && iconToUse && Icon[iconToUse];
  const StartIconComponent = !isIconOnly && startIcon && Icon[startIcon];
  const EndIconComponent = !isIconOnly && endIcon && Icon[endIcon];
  
  // Determine icon size based on button size
  const iconSize = getIconSize(size);
  
  // For icon-only buttons, require an aria-label for accessibility
  if (isIconOnly && !rest['aria-label']) {
    console.warn('Icon-only buttons should have an aria-label for accessibility');
  }
  
  const target = newTab ? '_blank' : undefined;
  const rel = newTab ? 'noopener noreferrer' : undefined;
  
  // Filter out props that shouldn't be passed to DOM
  const { disabled, ...otherRest } = rest;
  
  // Create the button element
  const buttonElement = (
    <StyledLink
      href={href}
      target={target}
      rel={rel}
      onClick={onClick}
      $variant={variant}
      $size={size}
      $themeColor={themeColor}
      $bgColor={bgColor}
      $isLoading={isLoading}
      $fullWidth={!isIconOnly && fullWidth}
      $iconOnly={isIconOnly}
      $square={square}
      {...otherRest}
    >
      {isIconOnly ? (
        <IconContainer>
          {IconComponent && <IconComponent size={size} />}
        </IconContainer>
      ) : (
        <>
          {StartIconComponent && (
            <IconContainer>
              <StartIconComponent size={iconSize} />
            </IconContainer>
          )}
          
          {typeof children === 'string' ? (
            <Typo.MediumText fontWeight='500' $textAlign='center'>
              {children}
            </Typo.MediumText>
          ) : (
            children
          )}
          
          {EndIconComponent && (
            <IconContainer>
              <EndIconComponent size={iconSize} />
            </IconContainer>
          )}
        </>
      )}
    </StyledLink>
  );

  // For icon-only buttons, wrap in a tooltip if there's an aria-label
  if (isIconOnly && rest['aria-label']) {
    return (
      <Tooltip title={rest['aria-label']} placement={tooltipPlacement}>
        {buttonElement}
      </Tooltip>
    );
  }

  // Return regular button for non-icon buttons
  return buttonElement;
};

export default ButtonLink;